import { FC } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

// Services and Interfaces
import { IRecipe } from "@/interfaces/recipe/recipe";
import { IRecipeForm, recipeFormSchema } from "@/interfaces/recipe/recipe_form";

// Components
import RecipeForm from "./recipe_form";
import Modal from "@/components/modal/modal";
import { Add, ArrowRight } from "@carbon/icons-react";


interface RecipeModalProps {
    open: boolean;
    onClose: () => void;
    recipe?: IRecipe;
    isLoading?: boolean;
    onSave: (data: IRecipe) => void;
}

const RecipeModal: FC<RecipeModalProps> = ({
    open,
    onClose,
    recipe,
    isLoading = false,
    onSave,
}) => {
    const { t } = useTranslation();

    const formMethods = useForm<IRecipeForm>({
        resolver: zodResolver(recipeFormSchema),
        mode: 'onBlur',
        defaultValues: {
            title: recipe?.title ?? '',
        }
    });

    const handleClose = () => {
        formMethods.reset();
        onClose();
    }

    return (
        <Modal
            open={open}
            onClose={formMethods.formState.isDirty ? undefined : onClose}
            title={recipe ? recipe.title : t('modals.addRecipe')}
            children={
                <FormProvider {...formMethods}>
                    <RecipeForm />
                </FormProvider>
            }
            action1={{
                label: recipe ? t('components.buttons.save') : t('components.buttons.add'),
                icon: recipe ? <ArrowRight /> : <Add />,
                disabled: !formMethods.formState.isDirty,
                loading: isLoading,
                onClick: formMethods.handleSubmit(onSave),
            }}
            cancel={{
                label: t('components.buttons.cancel'),
                disabled: isLoading,
                onClick: handleClose
            }}
        />
    )
}

export default RecipeModal;